import { ReactNode } from "react";
import { useBranding } from "../lib/branding";

interface Props {
    /** Document heading, e.g. "TAX INVOICE" or "TIMESHEET". */
    kind: string;
    number: string;
    onClose: () => void;
    /** mailto target when the document has a natural recipient. */
    emailTo?: string | null;
    emailSubject?: string;
    children: ReactNode;
}

/**
 * A4-style document overlay with the brand header, printable as-is.
 *
 * "Download PDF" is the browser's print-to-PDF — deliberate, not a shortcut:
 * a server-side PDF pipeline is a dependency and a queue for a document the
 * browser already renders perfectly, and every OS ships print-to-PDF. The
 * print stylesheet hides everything except the document itself.
 *
 * Email opens the user's own mail client with the recipient and subject
 * prefilled. This system has no SMTP configured, and silently pretending to
 * send would be worse than handing the user their real mail client.
 */
export const PrintDoc = ({ kind, number, onClose, emailTo, emailSubject, children }: Props) => {
    const branding = useBranding();

    return (
        <div className="print-overlay">
            <div className="print-toolbar no-print">
                <strong>{number}</strong>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    {emailTo && (
                        <a className="btn ghost"
                           href={`mailto:${emailTo}?subject=${encodeURIComponent(emailSubject ?? `${kind} ${number}`)}&body=${encodeURIComponent(`Please find ${kind.toLowerCase()} ${number} attached.\n\nSave the document as PDF (Print > Save as PDF) and attach it before sending.`)}`}>
                            Email…
                        </a>
                    )}
                    <button className="btn" onClick={() => window.print()}>Print / Save as PDF</button>
                    <button className="btn ghost" onClick={onClose}>Close</button>
                </div>
            </div>

            <div className="print-doc">
                <header className="doc-head">
                    <div className="doc-brand">
                        <img src={branding.logoUrl ?? "/brand/icon.png"} alt="" height={44} />
                        <div>
                            <div className="doc-org">{branding.orgName}</div>
                            <div className="doc-sub">Perth, Western Australia</div>
                        </div>
                    </div>
                    <div className="doc-kind">
                        <div className="doc-kind-label">{kind}</div>
                        <div className="doc-number">{number}</div>
                    </div>
                </header>
                {children}
                <footer className="doc-foot">
                    Generated by {branding.orgName} — Powered by TrustCare
                </footer>
            </div>
        </div>
    );
};
